/**
 * HITL 8.0.0 lobby assist requests: payload shapes from the assist-request wire
 * contract (gpt-context `documents/assist-requests.md`, schema_version 1.0).
 *
 * The AI keeps the order and asks for a few seconds of help; task-router's
 * assist pool assigns the request to several idle lobby agents at once, and
 * each answers on their own.
 * An answer is never a takeover: nothing here carries cart or control state.
 *
 * Generalised from the MiniHITL `asr_disagreement` types (!1779): the
 * per-type `body` keeps that shape for type 1, everything else is common.
 */

export const ASSIST_SCHEMA_VERSION = '1.0';

/** Every assist request type in the contract; RENDERABLE_ASSIST_TYPES is what this console shows. */
export type AssistRequestType =
  | 'hear_and_confirm'
  | 'approve_quantity'
  | 'find_item'
  | 'fix_cart'
  | 'question';

/**
 * observe  recorded only; never offered to an agent
 * log      offered; gpt-context logs the answer but does not act on it
 * apply    offered; gpt-context acts on the answer
 */
export type AssistRequestMode = 'observe' | 'log' | 'apply';
export type AssistRequestPriority =
  | 'guest_waiting'
  | 'turn_in_flight'
  | 'after_the_fact';

/**
 * The bus envelope every event rides in (contract §3). `agent_id` is not part
 * of the assist contract: the bus may fill it, nothing here reads it.
 */
export interface IAssistEnvelope<T = any> {
  seq?: number;
  id?: string;
  timestamp?: string;
  ws_timestamp?: string;
  session_id?: string;
  agent_id?: string;
  agent_type?: string;
  restaurant_code?: string;
  /** Omitted when there is none. Never an explicit null. */
  lane_id?: string;
  /** `DEPLOY_ENV`, on what the console posts to the event API. */
  environment?: string;
  event: string;
  data: T;
  metadata?: Record<string, unknown>;
}

/** Every assist event's `data` carries these. */
export interface IAssistDataBase {
  schema_version: string;
  request_id: string;
  type: AssistRequestType | string;
}

export interface IAssistTranscriptLine {
  who: 'AI' | 'CX' | string;
  text: string;
  /** ISO-8601 UTC on gpt-context's clock; compare only with other server times. */
  timestamp: string;
}

export interface IAssistCartLine {
  display: string;
}

export interface IAssistRequestContext {
  /** Last ≤ 10 lines; always includes the AI's last line. */
  transcript: IAssistTranscriptLine[];
  cart: IAssistCartLine[];
}

/** `assist_request.data` (gpt-context -> store channel, contract §4.1). */
export interface IAssistRequest<B = unknown> extends IAssistDataBase {
  mode: AssistRequestMode;
  language: string;
  priority: AssistRequestPriority;
  /**
   * ISO-8601 UTC on gpt-context's clock, for the servers. The console never
   * reads it: it counts down from the assignment's `remaining_ms`.
   */
  deadline_at: string;
  /** The card's headline, ≤ 200 chars. */
  question: string;
  context: IAssistRequestContext;
  body: B;
}

/**
 * Store routing on every task-router pool event (contract §4.4). Pool events
 * ride the agent task channel, so the envelope's `restaurant_code` is
 * `TASK_CHANNEL_<id>`; the request's store travels in `data` instead.
 */
export interface IAssistPoolRoute {
  store_restaurant_code: string;
  /** Omitted when there is none. Never an explicit null. */
  lane_id?: string;
}

export type AssistPoolData<T> = T & IAssistPoolRoute;

/**
 * `assist_request_assigned` (task-router -> each agent it picked): the only
 * event task-router sends the console. Carries the whole request: the card
 * shows at once, with no claim step. The same request goes to several agents,
 * and each answers on their own, or not at all.
 */
export interface IAssistRequestAssigned extends IAssistDataBase {
  agent_id: string;
  remaining_ms: number;
  /** The full `assist_request.data`, verbatim. */
  request: IAssistRequest;
}

/**
 * What the agent clicked, by box: the hearing (`alt_N` = the Nth entry of
 * `primary.nbest`, 1-based), or one of the two non-answers.
 */
export type AssistAnswerChoice =
  | 'primary'
  | 'secondary'
  | 'alt_1'
  | 'alt_2'
  | 'alt_3'
  | 'noise'
  | 'unclear';

/**
 * `assist_answer` (console -> `POST /v1/event`, §4.5; gpt-context reads it).
 * One click is final, so one answer per agent per request, keyed by
 * (`request_id`, `agent_id`); `answer_id` dedupes a retried post.
 */
export interface IAssistAnswer extends IAssistDataBase {
  agent_id: string;
  answer_id: string;
  answer_choice: AssistAnswerChoice;
  /** The words on the clicked box; null for `noise` / `unclear`. */
  answer_text: string | null;
  /** Local clock: card on screen to the click. */
  decision_ms: number;
}

// ---- Type 1: hear_and_confirm (contract §5.1) --------------------------------

/** One engine's hearing of the disputed turn. Slots are by role, not vendor. */
export interface IAsrHearing {
  /** Display/data label only (`azure`, `assemblyai`); nothing branches on it. */
  engine: string;
  /** Normalised: what the card shows. */
  text: string;
  /** Null when the engine gives none. */
  confidence: number | null;
  /** ≤ 3; may be empty. `alt` answers index into `primary.nbest`. */
  nbest: string[];
}

export interface IHearAndConfirmBody {
  winner: 'primary' | 'secondary';
  primary: IAsrHearing;
  secondary: IAsrHearing;
  diff: {
    tokens: [string, string][];
    reason: 'numbers' | 'negation' | string;
  };
  /**
   * Keys into live-transcriber's 60 s buffer. The console does not read it:
   * task-router passes it to live-transcriber, which pushes the clip to this
   * agent's task channel (`assist_audio_clip`, contract D11).
   */
  live_audio: { turn_orders: number[]; stream_epoch: number } | null;
  /** Span in the session recording, for data and training; the console ignores it. */
  recording: {
    start_ms: number;
    end_ms: number;
    focus_start_ms: number;
    focus_end_ms: number;
  } | null;
}

/** The kinds of box on the hear-and-confirm card (`edited`: a typed answer, deferred). */
export type HearAndConfirmChoice =
  | 'primary'
  | 'secondary'
  | 'alt'
  | 'edited'
  | 'noise'
  | 'unclear';
